// @flow

import { filter, map, prop, view } from "ramda";
import { Maybe } from "ramda-fantasy";
import moment from "moment";

import type {
  DataBase,
  Dominio,
  MedidaSelecionada,
  EdicaoMedida,
  AtivoSelecionado,
  Id,
  ControleArquivos,
  FuncaoQtLink,
  ModoApp,
} from "../model";
import {
  ID_AJUSTE_DIAS_NORMAL,
  ID_AJUSTE_DIAS_BANCO,
  MODO_APP_SERIES,
  ID_INDICADOR_FINANCEIRO_CAPEX,
  VALOR_DEFAULT_JANELA_MOVEL_VISIVEL,
  MEDIDAS_COM_DEFAULT_JANELA_MOVEL_4,
} from "../model";
import {
  type GrupamentosMedida,
  type MedidaParaPersonalizar,
  type MedidaUsuarioTipo,
  type ConfiguracaoGlobalRelatorio,
  VALOR_DEFAULT_LAMBDA,
  VALOR_DEFAULT_HORIZONTE_DU,
  VALOR_DEFAULT_CONFIANCA,
  VALOR_DEFAULT_TOLERANCIA_DEMONSTRACAO_FINANCEIRA,
  TAMANHO_DEFAULT_JANELA_TIPO_DIAS_UTEIS,
  QUANTIDADE_DEFAULT_JANELA,
  ID_TIPO_JANELA_DIAS_UTEIS,
  GRUPAMENTO_MEDIDAS_QUANTITATIVAS,
  VALOR_DEFAULT_LIMITE,
  VALOR_DEFAULT_CORRETOR_MONETARIO,
  VALOR_DEFAULT_DEFLATOR,
  VALOR_DEFAULT_DIAS_UTEIS_PERIODICIDADE_NUMERADOR,
  VALOR_DEFAULT_DIAS_UTEIS_PERIODICIDADE_VOLATILIDADE,
  TIPO_RETORNO_ESTATISTICA_DISCRETO,
  PERIODICIDADE_ESTATISTICA_ANUAL,
  ESTIMACAO_VOLATILIDADE_AMOSTRA,
  TIPO_CALCULO_NUMERADOR_ARITMETICO,
  TIPO_RETORNO_NUMERADOR_MEDIO,
  TIPO_VOLATILIDADE_ATIVO,
  ID_CONFIGURACAO_RELATORIO_MEDIDA_NOME,
  ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_COMPOSTA,
  ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_USUARIO,
  ID_TIPO_AJUSTE_PRECO_PADRAO,
  MODO_APP_NORMAL,
  ID_FONTE_FIDC_PADRAO,
  ID_PERIODO_NOS_ULTIMOS_10_DIAS_UTEIS,
  FORMA_VISUALIZACAO_SERIES_POR_ATIVOS,
} from "../model";
import {
  getCamposPorMedida,
  getMedidasIndexadasPorId,
  getTrimestreBaseEmpresasAbertas,
  getAnoBaseEmpresasAbertas,
  getModoApp,
  getExibirConfiguracoesAvancadas,
} from "../state";
import { dataBaseDefaultLens } from "./lenses";
import {
  getIdMedidaSelecionada,
  getIndiceMedidaEditada,
  getIsIndicadorFinanceiroSelecionado,
  isSomenteAnoETrimestreBaseEmpresasAbertasSelecionados,
} from "./selectors/edicaoMedidaSelectors";

export type MapEdicaoMedida = {
  indiceMedidaEditada: ?number,
  idGrupoMedidasSelecionadas: ?number,
  GLOBAL: EdicaoMedida,
  [chave: GrupamentosMedida]: EdicaoMedida,
};

type MapaI18n = {
  [chave: string]: string,
};

export type IdiomaUsuario = "pt-BR" | "es-ES" | "en-US" | "fr-FR";

export type TipoAtivo =
  | "FI"
  | "FIDC"
  | "FII"
  | "FIP"
  | "PGBL_VGBL"
  | "RPPS"
  | "EFPC"
  | "LATAM"
  | "EUROPA"
  | "EUA"
  | "HEDGE_FUNDS"
  | "ACOES"
  | "RENDA_FIXA"
  | "INDICES";

export type MapConjuntoPermissaoMedidasPorTipoAtivo = {
  [chave: TipoAtivo]: Array<Id>,
};

export type DetalhesUsuario = {
  idioma: IdiomaUsuario,
  permissoesMedidasPorTipoAtivo: MapConjuntoPermissaoMedidasPorTipoAtivo,
  tiposDeAtivosPermitidos: Array<TipoAtivo>,
};

type ConfiguracaoSeries = {
  identificadores: Array<Id>,
  exibirDatasSemNegociacao: boolean,
  modoVisualizacao: number,
};

export type ModoDeBuscaDeAtivoExcel = "ATIVO" | "GRUPO";

export type IntegracaoExcel = {
  ativo: string,
  modoDeBuscaDoAtivo: ModoDeBuscaDeAtivoExcel,
  jwt: ?string,
  medidasSuportadas: ?Set<Id>,
};

type ColumnID = string;

// TODO: Tipar corretamente
export type GridState = {
  aggregation: Array<*>,
  filter: Array<*>,
  fixedLeft: Array<*>,
  fixedRight: Array<*>,
  order: Array<ColumnID>,
  sort: Array<*>,
  formaDeVisualizacaoRelatorioSeries: ?Id,
};

export type State = {|
  +appIniciada: boolean,
  +gerandoRelatorio: boolean,
  +modoApp: ModoApp,
  +erroInicializacao?: ?string,
  +dominio: Dominio,
  +dataBase: ?DataBase,
  +usuario: DetalhesUsuario,
  +edicaoMedida: MapEdicaoMedida,
  +configuracaoSeries: ConfiguracaoSeries,
  +isAxis: boolean,
  +i18n: MapaI18n,
  +grupamentoMedidaSelecionado: GrupamentosMedida,
  +alterouGrupamentoMedidaSelecionado: boolean,
  +filtrosTipoAtivoSelecionados: Array<Id>,
  +medidasSelecionadas: Array<MedidaSelecionada>,
  +medidasParaPersonalizar: Array<MedidaParaPersonalizar>,
  +funcaoQtLink: FuncaoQtLink,
  +ativosSelecionados: Array<AtivoSelecionado>,
  +arquivosMedidas: ControleArquivos,
  +seletoresAbertos: { [string]: boolean },
  +isFechamentoDeMes: boolean,
  +medidasSelecionadasDesabilitada: boolean,
  +medidasFixas: Array<Id>,
  +integracaoExcel: IntegracaoExcel,
  +medidasUsuario: Array<MedidaUsuarioTipo>,
  +gridState: GridState,
  +restaurandoConfiguracoes: boolean,
  +configuracoesRelatorioSalvo: ?ConfiguracaoGlobalRelatorio,
|};

export type SimpleSelector<T> = (state: State) => T;

export * from "./resultado";

export function getInitialEdicaoMedida(
  state: ?State = initialState,
  grupamento: ?GrupamentosMedida,
  medida: ?Id
): EdicaoMedida {
  const dataBaseDefault = state
    ? moment(view(dataBaseDefaultLens, state))
    : null;
  const trimestreBaseEmpresasAbertas = state
    ? getTrimestreBaseEmpresasAbertas(state)
    : null;
  const anoBaseEmpresasAbertas = state
    ? getAnoBaseEmpresasAbertas(state)
    : null;
  const modoApp = state ? getModoApp(state) : null;
  const defaultPeriodo =
    modoApp && modoApp === MODO_APP_SERIES
      ? [ID_PERIODO_NOS_ULTIMOS_10_DIAS_UTEIS]
      : [];
  const janelaMovelPadrao = () => {
    const avancadoAberto = state && getExibirConfiguracoesAvancadas(state);
    return avancadoAberto && modoApp === MODO_APP_SERIES
      ? (medida !== null &&
        medida !== undefined &&
        MEDIDAS_COM_DEFAULT_JANELA_MOVEL_4.includes(medida)
          ? 4
          : VALOR_DEFAULT_JANELA_MOVEL_VISIVEL) ||
          VALOR_DEFAULT_JANELA_MOVEL_VISIVEL
      : 1;
  };

  const exibirConfiguracoesAvancadas = () => {
    const isAvancadoAberto = state && getExibirConfiguracoesAvancadas(state);
    return isAvancadoAberto ? true : true; // Se o checkbox de config avancadas voltar, mudar para true : false
  };

  const isAjusteNormal =
    state &&
    modoApp === MODO_APP_NORMAL &&
    getIdMedidaSelecionada(state) === medida &&
    getIsIndicadorFinanceiroSelecionado(state) &&
    !isSomenteAnoETrimestreBaseEmpresasAbertasSelecionados(state) &&
    !getIndiceMedidaEditada(state);

  const isMedidaIndicadorCapex =
    state && medida === ID_INDICADOR_FINANCEIRO_CAPEX;

  return {
    medida: null,
    alias: null,
    periodicidade: 0,
    periodos: defaultPeriodo,
    ajusteDias: isAjusteNormal ? ID_AJUSTE_DIAS_NORMAL : ID_AJUSTE_DIAS_BANCO,
    dataInicial: null,
    dataFinal: dataBaseDefault,
    anos: (anoBaseEmpresasAbertas && [anoBaseEmpresasAbertas]) || [],
    tipoReferencia: 1,
    trimestreReferencia: trimestreBaseEmpresasAbertas,
    anoReferencia: anoBaseEmpresasAbertas,
    comparacao: 5,
    tipoConta: 3,
    versaoConta: 2,
    formaAcumulacao: isMedidaIndicadorCapex ? 2 : 3,
    capitalUtilizado: 3,
    toleranciaDF: VALOR_DEFAULT_TOLERANCIA_DEMONSTRACAO_FINANCEIRA,
    lambda: VALOR_DEFAULT_LAMBDA,
    ativoSemRisco: null,
    taxaDeAdministracao: 0,
    horizonte: VALOR_DEFAULT_HORIZONTE_DU,
    confianca: VALOR_DEFAULT_CONFIANCA,
    volatilidade: 0,
    tipoDoRetorno: 0,
    benchmark: null,
    competencia: null,
    indice: null,
    indices: ["CDI", "Dólar", "Ibovespa", "IRF-M"],
    frequencias: (trimestreBaseEmpresasAbertas && [1, 2, 3, 4]) || [],
    plUtilizado: 3,
    toleranciaDiasValorMercado: 22,
    corretorMonetario: VALOR_DEFAULT_CORRETOR_MONETARIO,
    deflator: VALOR_DEFAULT_DEFLATOR,
    valorBaseDeflator: null,
    valorBaseDeflatorOutro: null,
    dataBaseDeflator: null,
    dataBaseDeflatorDataEspecifica: null,
    dataEspecifica: null,
    dataInicialComposta: null,
    dataFinalComposta: null,
    tamanhoJanela: TAMANHO_DEFAULT_JANELA_TIPO_DIAS_UTEIS,
    quantidadeJanelas: QUANTIDADE_DEFAULT_JANELA,
    tipoJanela: ID_TIPO_JANELA_DIAS_UTEIS,
    datasUteisSelecaoJanela: null,
    mesesSelecaoJanela: null,
    valorComboMedidas: "",
    indiceMedidaEditada: null,
    tipoCalculo: 0,
    colunasRanking: [1],
    pesosRanking: {},
    nome: "",
    formato: null,
    expressao: "",
    ativos: [],
    campos: [],
    compradores: 5,
    visualizacaoCarteira: "INDIVIDUAL",
    limite: VALOR_DEFAULT_LIMITE,
    retornoNumerador: TIPO_RETORNO_NUMERADOR_MEDIO,
    calculoNumerador: TIPO_CALCULO_NUMERADOR_ARITMETICO,
    periodicidadeNumerador: PERIODICIDADE_ESTATISTICA_ANUAL,
    tipoVolatilidade: TIPO_VOLATILIDADE_ATIVO,
    estimacaoVolatilidade: ESTIMACAO_VOLATILIDADE_AMOSTRA,
    periodicidadeVolatilidade: PERIODICIDADE_ESTATISTICA_ANUAL,
    diasUteisPeriodicidadeNumerador: VALOR_DEFAULT_DIAS_UTEIS_PERIODICIDADE_NUMERADOR,
    diasUteisPeriodicidadeVolatilidade: VALOR_DEFAULT_DIAS_UTEIS_PERIODICIDADE_VOLATILIDADE,
    tipoRetornoConfiguracoesEstatistica: TIPO_RETORNO_ESTATISTICA_DISCRETO,
    idMedidaUsuario: [],
    ajustesPreco: [ID_TIPO_AJUSTE_PRECO_PADRAO],
    ajustesPrecoSemProventos: [ID_TIPO_AJUSTE_PRECO_PADRAO],
    fonteFidc: ID_FONTE_FIDC_PADRAO,
    exibirConfiguracoesAvancadas: exibirConfiguracoesAvancadas(),
    janelaMovel: janelaMovelPadrao(),
    ultimaNegociacaoMes: false,
    adicionarDataReferencia: false,
  };
}

export const getInitialEdicaoMedidaComMedida = (medida: ?Id, state: State) => {
  const campos = Maybe(medida)
    .chain(id => Maybe(getMedidasIndexadasPorId(state)[id]))
    .chain(medida => Maybe(medida.medidasSecundarias))
    .map(map(({ codigoOriginal: id }) => getCamposPorMedida(state)[id]))
    .map(filter(Boolean))
    .map(filter(prop("checked")))
    .map(map(prop("idMedida")))
    .getOrElse([]);

  return {
    ...getInitialEdicaoMedida(
      state,
      medida
        ? getMedidasIndexadasPorId(state)[medida].grupamentoMedida.nome
        : null,
      medida
    ),
    campos,
    medida,
  };
};

export const getInitialEdicaoMedidaPersonalizada = (state: ?State) => {
  const modoApp = state ? getModoApp(state) : null;
  const idMedidaPersonalizada =
    modoApp === MODO_APP_SERIES
      ? ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_COMPOSTA
      : ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_USUARIO;

  return {
    ...getInitialEdicaoMedida(state, "PERSONALIZADAS"),
    medida: idMedidaPersonalizada,
  };
};

export const initialState: State = {
  dominio: {
    camposMedidasSecundarias: [],
    medidas: [],
    periodos: [],
    periodicidades: [],
    ajustesDias: [],
    filtrosTipoAtivo: [],
    periodosPorTipoLista: {},
    modosExibicao: [],
    tiposConta: [],
    versoesConta: [],
    formasAcumulacao: [],
    capitaisUtilizados: [],
    anos: [],
    tiposReferencia: [],
    tiposDataReferencia: [],
    trimestresReferencia: [],
    frequeciasIndicador: [],
    separadorDecimal: "",
    ativosSemRisco: [],
    tiposDoRetorno: [],
    volatilidades: [],
    taxasDeAdministracao: [],
    indices: [],
    mesesEAnosCompostos: [],
    plsUtilizado: [],
    corretoresMonetarios: [],
    deflatores: [],
    datasBaseDeflator: [],
    valoresBaseDeflator: [],
    dataBaseDefault: "",
    dataBaseMaxima: "",
    tiposJanela: [],
    grupamentosMedida: [],
    diasUteis: [],
    benchmarksPersonalizados: [],
    dataFechamentoMesAnterior: null,
    trimestreEAnoBaseEmpresasAbertas: null,
    funcoesRanking: [],
    formatacaoMedidasCompostas: [],
    formasVisualizacaoCarteira: [],
    tiposRetornoNumerador: [],
    tiposCalculoNumerador: [],
    periodicidadeEstatistica: [],
    tiposVolatilidade: [],
    estimacaoVolatilidade: [],
    tiposRetornoEstatistica: [],
    tiposAjuste: [],
    tiposAjusteSemProventos: [],
    escalas: [],
    fontesDeDadosFIDC: [],
    formasDeVisualizacaoRelatorioSeries: [],
  },
  dataBase: null,
  usuario: {
    idioma: "pt-BR",
    tiposDeAtivosPermitidos: [],
    permissoesMedidasPorTipoAtivo: {},
  },
  i18n: {},
  isAxis: true,
  gerandoRelatorio: false,
  edicaoMedida: {
    indiceMedidaEditada: null,
    idGrupoMedidasSelecionadas: null,
    QUANTITATIVA: getInitialEdicaoMedida(null, "QUANTITATIVA"),
    QUALITATIVA: getInitialEdicaoMedida(null, "QUALITATIVA"),
    SERIES: getInitialEdicaoMedida(null, "SERIES"),
    EMPRESAS: getInitialEdicaoMedida(null, "EMPRESAS"),
    PERSONALIZADAS: getInitialEdicaoMedidaPersonalizada(null),
    GLOBAL: getInitialEdicaoMedida(),
  },
  configuracaoSeries: {
    exibirDatasSemNegociacao: false,
    identificadores: [0],
    modoVisualizacao: FORMA_VISUALIZACAO_SERIES_POR_ATIVOS,
  },
  appIniciada: false,
  modoApp: MODO_APP_NORMAL,
  grupamentoMedidaSelecionado: GRUPAMENTO_MEDIDAS_QUANTITATIVAS,
  alterouGrupamentoMedidaSelecionado: false,
  filtrosTipoAtivoSelecionados: [],
  medidasSelecionadas: [],
  medidasParaPersonalizar: [],
  funcaoQtLink: {
    conteudo: "",
    carregando: false,
  },
  ativosSelecionados: [],
  arquivosMedidas: {
    listaArquivos: [],
    exibirModalArquivosMedidas: false,
    arquivoEmCache: null,
    salvandoArquivo: false,
    permiteCompartilhamento: true,
    carregandoArquivo: false,
  },
  seletoresAbertos: {},
  isFechamentoDeMes: false,
  medidasSelecionadasDesabilitada: false,
  medidasFixas: [ID_CONFIGURACAO_RELATORIO_MEDIDA_NOME],
  medidasUsuario: [],
  integracaoExcel: {
    ativo: "",
    modoDeBuscaDoAtivo: "ATIVO",
    jwt: null,
    medidasSuportadas: null,
  },
  gridState: {
    aggregation: [],
    filter: [],
    order: [],
    sort: [],
    group: [],
    fixedLeft: [],
    fixedRight: [],
    formaDeVisualizacaoRelatorioSeries: null,
  },
  restaurandoConfiguracoes: false,
  configuracoesRelatorioSalvo: null,
};

export * from "./actions";
export * from "./selectors/comum";
export * from "./selectors/dataBaseSelectors";
export * from "./selectors/dominioSelectors";
export * from "./selectors/grid";
export * from "./selectors/i18n";
export * from "./selectors/integracaoExcel";
export * from "./selectors/edicaoMedidaSelectors";
export * from "./selectors/medidaParaPersonalizarSelector";
export * from "./selectors/medidaSelecionadaSelectors";
export * from "./selectors/medidaUsuarioSelectors";
export * from "./selectors/funcaoQtLinkSelectors";
export * from "./selectors/periodosSelectors";
export * from "./selectors/ativosSelecionadosSelectors";
export * from "./selectors/filtroMedidasSelectors";
export * from "./selectors/arquivosMedidasSelectors";
export * from "./selectors/usuarioSelectors";
export * from "./selectors/configuracaoRelatorio";
export * from "./selectors/series";
export * from "./reducer";
export * from "./lenses";
